import { useState, useEffect, useRef } from "react";
import MultilineInput from "./MultilineInput";
import { environment } from "../../../config/config";

const Chat = ({ messages, setMessages, onClose, selectedProject }) => {
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [currentTypingMessage, setCurrentTypingMessage] = useState(null);
  const [displayedText, setDisplayedText] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const chatRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Dummy data for responses
  const dummyData = [
    {
      question: "status",
      answer:
        "Your material request is currently being processed. It's about 65% complete and should be fulfilled by the end of the week.",
    },
    {
      question: "pending",
      answer:
        "You have 3 pending material requests. Two are in the procurement stage and one is awaiting approval.",
    },
    {
      question: "deadline",
      answer:
        "The deadline for your current material request is this Friday at 3 PM. All approvals should be completed by Thursday afternoon.",
    },
    {
      question: "process",
      answer:
        "The material request process involves 4 steps: submission, approval, procurement, and delivery. Your request is currently in the procurement stage.",
    },
    {
      question: "help",
      answer:
        "I can help you check the status of requests, create new material requests, track deliveries, or connect you with a procurement specialist.",
    },
  ];

  // Send welcome message when component mounts
  useEffect(() => {
    if (messages.length === 0) {
      setTimeout(() => {
        const welcomeMessage = {
          sender: "Staart AI",
          text: "Hello! How can I help with your material request today?",
        };
        setCurrentTypingMessage(welcomeMessage);
      }, 1000);
    }
  }, []);

  // Format the text for display - handles bold text with ** markers
  const formatTextWithMarkdown = (text) => {
    if (!text) return "";

    // Replace markdown-style bold formatting (**text**) with HTML bold tags
    const formattedText = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    return formattedText;
  };

  // Handle typing effect
  useEffect(() => {
    if (!currentTypingMessage) return;

    let fullText = currentTypingMessage.text;
    let charIndex = 0;
    setDisplayedText("");
    setIsTyping(true);

    const typingInterval = setInterval(() => {
      if (charIndex < fullText.length) {
        setDisplayedText((prev) => prev + fullText.charAt(charIndex));
        charIndex++;
      } else {
        clearInterval(typingInterval);

        // If this is the waiting message, keep it in the current typing state
        if (currentTypingMessage.isWaiting) {
          // Keep the message as typing, don't add to messages array yet
        } else {
          // For regular messages, add to messages array and reset
          setMessages((prev) => [...prev, currentTypingMessage]);
          setCurrentTypingMessage(null);
          setDisplayedText("");
          setIsTyping(false);
        }
      }
    }, 30); // Adjust typing speed here

    return () => clearInterval(typingInterval);
  }, [currentTypingMessage]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    chatRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, displayedText]);

  const cancelRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Reset states
    setIsFetching(false);
    setIsTyping(false);

    // Remove any waiting messages
    setMessages((prev) => prev.filter((msg) => !msg.isWaiting));
    setCurrentTypingMessage({
      text: "Request canceled. What else can I help you with?",
      sender: "Staart AI",
    });
  };

  const sendMessage = () => {
    if (!input.trim() || isTyping) return;

    // If currently fetching, cancel the request instead
    if (isFetching) {
      cancelRequest();
      return;
    }

    // Add user message
    setMessages([...messages, { sender: "You", text: input }]);

    // Store the user input and clear the input field
    const userInput = input;
    setInput("");

    // Show "thinking..." with typing effect and animation during API wait time
    setCurrentTypingMessage({
      text: "thinking...",
      sender: "Staart AI",
      isWaiting: true,
    });

    // Set fetching state to true
    setIsFetching(true);

    // setTimeout(() => {
    //   abortControllerRef.current = new AbortController();
    //   const { signal } = abortControllerRef.current;

    //   // Make API request to get AI response
    //   // fetch("http://127.0.0.1:5000/ask", {
    //   fetch("https://us-central1-charming-shield-300804.cloudfunctions.net/materialRequestChat/ask", {
    //     method: "POST",
    //     headers: {
    //       "Content-Type": "application/json",
    //       "Authorization" : "Bearer eyJhbGciOiJSUzI1NiIsImtpZCI6ImMzN2RhNzVjOWZiZTE4YzJjZTkxMjViOWFhMWYzMDBkY2IzMWU4ZDkiLCJ0eXAiOiJKV1QifQ.eyJpc3MiOiJodHRwczovL2FjY291bnRzLmdvb2dsZS5jb20iLCJhenAiOiIzMjU1NTk0MDU1OS5hcHBzLmdvb2dsZXVzZXJjb250ZW50LmNvbSIsImF1ZCI6IjMyNTU1OTQwNTU5LmFwcHMuZ29vZ2xldXNlcmNvbnRlbnQuY29tIiwic3ViIjoiMTE2NzExNTQxODQ5MzA1OTAxNDUzIiwiaGQiOiJzdGFhcnQuY28uaW4iLCJlbWFpbCI6Imthc2h5YXBAc3RhYXJ0LmNvLmluIiwiZW1haWxfdmVyaWZpZWQiOnRydWUsImF0X2hhc2giOiJVQ3pUUUs2WGZxNDlpd28wMmRmaG13IiwiaWF0IjoxNzQ0NzE5ODQ1LCJleHAiOjE3NDQ3MjM0NDV9.noF25udb5Ca54iXZ4qrLXaV5qIqnbdNSe6zUjGgrVOh50EZglpf22jPZtgsm6DiqvfxeoDkjqnErXn2czgdk0Wajrf7HcUFn0lomICtda_rUM9tZBTENvpSzjKEJ__BrxxxZ-LDXa4fPBRkSVRI3V0Js09BfwteZEu2PZWqbMZq2hc4rZHvJAIk-sCWYzIZ5Xq3NhCnqYc2QPENyqgdAG8X2w7Vf5OHX5ySVM7kfM6RwkRV2MACJyAzOf-0cyTck0p0RzEmtHsNr-KXr1JCGuiO9Q_BWjVCjuWay7Yyr2_6dI8zLhg2DS3Sjtm8xmHAA4tj0m3TXIpA_Z2euM3YNaQ"
    //     },
    //     body: JSON.stringify({
    //       question: userInput,
    //       user_id: `${selectedProject.firmDetailsID}/MRNRequest/${selectedProject.projectID}`,
    //     }),
    //     signal: signal,
    //   })
    //     .then((response) => response.json())
    //     .then((data) => {
    //       console.log("data.answer : ", data.answer);
    //       let aiResponse = data.answer || "Sorry, I didn't understand that.";

    //       // Once we get the response, replace the "thinking" message with actual response
    //       setMessages((prev) => prev.filter((msg) => !msg.isWaiting)); // Remove any waiting messages
    //       setCurrentTypingMessage({ text: aiResponse, sender: "Staart AI" });
    //       setIsFetching(false);
    //     })
    //     .catch((error) => {
    //       // Don't show error if it was aborted by user
    //       if (error.name === 'AbortError') {
    //         console.log('Fetch aborted by user');
    //         return;
    //       }

    //       console.error("Error:", error);
    //       setMessages((prev) => prev.filter((msg) => !msg.isWaiting)); // Remove any waiting messages
    //       setCurrentTypingMessage({
    //         text: "Oops! Something went wrong. Please try again later.",
    //         sender: "Staart AI",
    //       });
    //       setIsFetching(false);
    //     });
    // }, 1000);

    setTimeout(() => {
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      fetch(
        environment === "production"
          ? "https://requestchatprod-303239150168.us-central1.run.app/ask"
          : "https://us-central1-charming-shield-300804.cloudfunctions.net/RequestChat/ask",
        // "https://us-central1-charming-shield-300804.cloudfunctions.net/materialRequestChat/ask",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question: userInput,
            user_id: `${selectedProject.firmDetailsID}/MRNRequest/${selectedProject.projectID}`,
          }),
          signal: signal,
        }
      )
        .then((response) => {
          // Check for HTTP error responses
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          console.log("data.answer: ", data.answer);
          let aiResponse = data.answer || "Sorry, I didn't understand that.";

          // Once we get the response, replace the "thinking" message with the actual response
          setMessages((prev) => prev.filter((msg) => !msg.isWaiting)); // Remove any waiting messages
          setCurrentTypingMessage({ text: aiResponse, sender: "Staart AI" });
          setIsFetching(false);
        })
        .catch((error) => {
          // Don't show error if it was aborted by user
          if (error.name === "AbortError") {
            console.log("Fetch aborted by user");
            return;
          }

          console.error("Error:", error);
          setMessages((prev) => prev.filter((msg) => !msg.isWaiting)); // Remove any waiting messages
          setCurrentTypingMessage({
            text: "Oops! Something went wrong. Please try again later.",
            sender: "Staart AI",
          });
          setIsFetching(false);
        });
    }, 1000);
  };

  // Custom icon components using inline SVGs
  const MessageCircleIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "inline-block" }}
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );

  const SendIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "inline-block" }}
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );

  const StopIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "inline-block" }}
    >
      <rect x="6" y="6" width="12" height="12" />
    </svg>
  );

  const CloseIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "inline-block" }}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  // Thinking animation dots component
  const ThinkingDots = () => (
    <span className="thinking-dots">
      <span className="dot"></span>
      <span className="dot"></span>
      <span className="dot"></span>
    </span>
  );

  return (
    <div
      style={{
        width: "100%",
        height: "calc(100vh - 190px)",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        borderRadius: "0.5rem",
        overflow: "hidden",
        border: "1px solid #e5e7eb",
        marginLeft: 5,
      }}
    >
      <div
        style={{
          backgroundColor: "#FFE9A2",
          color: "#353535",
          fontSize: "1.125rem",
          fontWeight: "600",
          padding: "0.75rem 1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <MessageCircleIcon /> Material Request Assistant
        </div>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            color: "#353535",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0.25rem",
          }}
        >
          <CloseIcon />
        </button>
      </div>

      {/* Messages container */}
      <div
        style={{
          flex: "1",
          overflowY: "auto",
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          backgroundColor: "#f9fafb",
        }}
      >
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              padding: "0.75rem",
              maxWidth: "70%",
              borderRadius: "0.5rem",
              backgroundColor: msg.sender === "You" ? "#FFE9A2" : "#e1e1e1",
              color: msg.sender === "You" ? "#353535" : "#374151",
              alignSelf: msg.sender === "You" ? "flex-end" : "flex-start",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              opacity: 1,
              transform: "translateY(0)",
              animation: "fadeIn 0.3s ease-in-out",
            }}
          >
            <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>
              {msg.sender}
            </span>
            <p
              style={{ margin: "0.25rem 0 0 0", lineHeight: "1.5" }}
              dangerouslySetInnerHTML={{
                __html: formatTextWithMarkdown(msg.text).replace(
                  /\n/g,
                  "<br/>"
                ),
              }}
            ></p>
          </div>
        ))}

        {/* Typing indicator */}
        {currentTypingMessage && (
          <div
            style={{
              padding: "0.75rem",
              maxWidth: "70%",
              borderRadius: "0.5rem",
              backgroundColor: "#e1e1e1",
              color: "#374151",
              alignSelf: "flex-start",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              animation: "fadeIn 0.3s ease-in-out",
            }}
          >
            <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>
              Staart AI
            </span>
            {currentTypingMessage.isWaiting ? (
              <p
                style={{
                  margin: "0.25rem 0 0 0",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                thinking <ThinkingDots />
              </p>
            ) : (
              <p
                style={{ margin: "0.25rem 0 0 0", lineHeight: "1.5" }}
                dangerouslySetInnerHTML={{
                  __html: formatTextWithMarkdown(displayedText).replace(
                    /\n/g,
                    "<br/>"
                  ),
                }}
              ></p>
            )}
          </div>
        )}

        <div ref={chatRef} />
      </div>

      {/* Input area */}
      <div
        style={{
          padding: "1rem",
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          gap: "0.5rem",
          backgroundColor: "white",
        }}
      >
        <MultilineInput
          input={input}
          setInput={setInput}
          sendMessage={sendMessage}
          disabled={isTyping && !isFetching}
        />
        <button
          onClick={sendMessage}
          style={{
            backgroundColor:
              isTyping && !isFetching
                ? "#9ca3af"
                : isFetching
                ? "#ff6b6b"
                : "#FFE9A2",
            color: "#353535",
            padding: "0.75rem",
            borderRadius: "0.375rem",
            border: "none",
            cursor: isTyping && !isFetching ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.2s ease",
            height: "40px",
            width: "40px",
          }}
          disabled={isTyping && !isFetching}
          title={isFetching ? "Stop generating" : "Send message"}
        >
          {isFetching ? <StopIcon /> : <SendIcon />}
        </button>
      </div>

      {/* CSS Animations */}
      <style jsx global>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes blink {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0;
          }
        }

        .thinking-dots {
          display: inline-flex;
          align-items: center;
          margin-left: 6px;
        }

        .dot {
          width: 6px;
          height: 6px;
          background-color: #374151;
          border-radius: 50%;
          margin: 0 2px;
          display: inline-block;
          animation: bounce 1.4s infinite ease-in-out both;
        }

        .dot:nth-child(1) {
          animation-delay: -0.32s;
        }

        .dot:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes bounce {
          0%,
          80%,
          100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default Chat;
